import { File, FileText, FileType2, Image, Paperclip } from "lucide-react";
import { Plan } from "src/domain/plan";
import { FREE_ATTACHMENT_LIMIT, PAID_ATTACHMENT_LIMIT } from "./Constants";
import applicationStore from "src/store/RootStore";

/**
 * Utility functions for handling attachments in threads
 */

/**
 * Returns the appropriate lucide-react icon component for a given file name
 * based on its extension. Used across chat attachments, docs-in-chat drawer,
 * and upload previews for consistent file-type iconography.
 *
 * @param {string} fileName - The file name including extension
 * @returns {import("lucide-react").LucideIcon} The icon component
 */
export const getFileIcon = (fileName) => {
  const ext = fileName?.split(".").pop()?.toLowerCase() || "";
  switch (ext) {
    case "png":
    case "jpg":
    case "jpeg":
    case "gif":
    case "svg":
    case "webp":
      return Image;
    case "pdf":
      return FileText;
    case "docx":
    case "doc":
      return FileType2;
    case "zip":
    case "rar":
    case "7z":
      return Paperclip;
    default:
      return File;
  }
};

/**
 * Counts the total number of attachments/documents in a thread
 * @param {Object} threadData - The thread data object from Redux state
 * @param {Object|Array} currentAttachments - Current attachments being uploaded (from state.messageSlice.attachments)
 * @returns {number} Total count of attachments in the thread
 */
export const getTotalAttachmentCount = (threadData, currentAttachments = {}) => {
  let count = 0;

  // Count current attachments being uploaded
  if (currentAttachments) {
    if (Array.isArray(currentAttachments)) {
      // Handle legacy array format
      count += currentAttachments.length;
    } else if (typeof currentAttachments === "object") {
      // Handle new object format
      count += Object.keys(currentAttachments).length;
    }
  }

  if (!threadData?.id) {
    // No thread exists yet, only count current attachments
    return count;
  }

  // Count reference document if exists (legacy single-file upload)
  count += threadData?.reference_docs?.length || 0;

  // Count attachments in messages (new multi-file system)
  // if (threadData?.messages && Array.isArray(threadData.messages)) {
  //   threadData.messages.forEach((message) => {
  //     if (message?.payload?.attachments && Array.isArray(message.payload.attachments)) {
  //       count += message.payload.attachments.length;
  //     }
  //   });
  // }

  // Return total count of attachments
  return count;
};

/**
 * Gets all attachment objects from a thread
 * @param {Object} threadData - The thread data object from Redux state
 * @param {Object|Array} currentAttachments - Current attachments being uploaded
 * @returns {Array} Array of all attachment objects
 */
export const getAllAttachments = (threadData, currentAttachments = {}) => {
  const attachments = [];

  // Add current attachments
  if (currentAttachments) {
    if (Array.isArray(currentAttachments)) {
      // Handle legacy array format
      attachments.push(
        ...currentAttachments.map((att) => ({
          ...att,
          source: "current",
        })),
      );
    } else if (typeof currentAttachments === "object") {
      // Handle new object format
      attachments.push(
        ...Object.values(currentAttachments).map((att) => ({
          ...att,
          source: "current",
        })),
      );
    }
  }

  // Add reference document
  if (threadData?.reference_doc) {
    attachments.push({
      ...threadData.reference_doc,
      source: "reference_doc",
    });
  }

  // Add attachments from messages
  if (threadData?.messages && Array.isArray(threadData.messages)) {
    threadData.messages.forEach((message, messageIndex) => {
      if (message?.payload?.attachments && Array.isArray(message.payload.attachments)) {
        message.payload.attachments.forEach((att, attIndex) => {
          attachments.push({
            ...att,
            source: "message",
            messageId: message.id || message.message_id,
            messageIndex,
            attachmentIndex: attIndex,
          });
        });
      }
    });
  }

  return attachments;
};

/**
 * Checks if the attachment limit has been reached
 * @param {Object} threadData - The thread data object from Redux state
 * @param {Object|Array} currentAttachments - Current attachments being uploaded
 * @param {number} limit - Maximum number of attachments allowed (default: 5)
 * @returns {boolean} True if limit is reached
 */
/**
 * Reads the server-provided attachment budget from the most recent attachment
 * message in the thread. Falls back to a tier-derived default when no attachment
 * messages exist yet (first upload in a new chat).
 *
 * Paid tiers get the paid limit via Plan.isPaid(). The legacy `court_document`
 * value the old inline set carried was dropped deliberately — the backend
 * migrated all legacy tier values server-side (subscription-tier-model), and
 * a stray holder fails toward the FREE limit, the safe direction.
 */

export const getNumFutureAttachmentsAvailable = (threadData, userMetadata) => {
  // Prefer the server-provided attachment_info (lives on the response, not in
  // message history). This reflects the user's *current* tier, so an upgrade
  // mid-chat immediately surfaces the bigger budget.
  const attachmentInfo = applicationStore.getState().messageSlice.attachmentInfo;
  const isPaid = Plan.from(userMetadata?.subscription_type).isPaid();
  if (attachmentInfo != null && typeof attachmentInfo.attachment_remaining === "number") {
    // Guard against stale attachment_info: if the user just upgraded to a paid
    // plan but the cached list-messages response still reflects the free-tier
    // limit (e.g. webhook lag), recalculate remaining based on the paid cap.
    if (isPaid && attachmentInfo.attachment_limit === FREE_ATTACHMENT_LIMIT) {
      const used = FREE_ATTACHMENT_LIMIT - attachmentInfo.attachment_remaining;
      return PAID_ATTACHMENT_LIMIT - used;
    }
    return attachmentInfo.attachment_remaining;
  }

  // Fallback: scan message history for the deprecated num_future_attachments_available
  // field (still present on old messages written before the migration).
  const messages = threadData?.messages;
  if (messages && Array.isArray(messages)) {
    for (let i = messages.length - 1; i >= 0; i--) {
      const msg = messages[i];
      if (
        msg?.payload?.attachments &&
        Array.isArray(msg.payload.attachments) &&
        typeof msg.payload.num_future_attachments_available === "number"
      ) {
        return msg.payload.num_future_attachments_available;
      }
    }
  }
  // No attachment messages yet — return tier-derived default.
  return isPaid ? PAID_ATTACHMENT_LIMIT : FREE_ATTACHMENT_LIMIT;
};

export const hasReachedAttachmentLimit = (threadData, userMetadata) => {
  return getNumFutureAttachmentsAvailable(threadData, userMetadata) <= 0;
};

/**
 * Resolves an attachment entry against the thread's messages by UUID and returns
 * the render state for its card. See openspec change
 * `fix-user-uploaded-attachment-rendering` for the state-machine rationale.
 *
 * Pairing key: `attachment.reference_doc_storage_id === document.reference_document_id`.
 * The backend invariant `document.reference_document_id != null ⟺ is_user_uploaded === true`
 * guarantees no collision with AI-generated docs.
 *
 * States (evaluated top-to-bottom):
 *   - "failed"     — attachment.status === "failed" (doc may be null)
 *   - "preparing"  — attachment.is_ai_parsing === true (AI parsing in flight)
 *   - "partial"    — paired doc exists + word_limit_exceeded OR page_limit_exceeded
 *   - "ready"      — paired doc exists, within limits
 *   - "processing" — no paired doc AND attachment.status === "processing"
 *   - "no_preview" — no paired doc, but the file is settled server-side (extraction
 *                    skipped / never produced a document card). TERMINAL, no spinner.
 *
 * The document check runs BEFORE the status check so that a stale "processing"
 * status (from a list-messages overwrite) cannot spin a card whose document
 * message has already arrived via WS.
 *
 * `.txt`/unsupported and email attachments (which never emit a `document` message by
 * design) fall through to "no_preview" — a non-spinner, non-failed terminal state.
 *
 * Pure function. No Redux, no side effects. Single source of truth = the messages array.
 *
 * @param {Array} messages - The thread's messages array
 * @param {Object} attachment - One entry from a message.payload.attachments
 * @returns {{ attachment: Object, doc: Object|null, state: "ready"|"processing"|"preparing"|"no_preview"|"partial"|"failed" }}
 */
export const resolveAttachment = (messages, attachment) => {
  if (!attachment) {
    return { attachment: null, doc: null, state: "failed" };
  }

  if (attachment.status === "failed") {
    return { attachment, doc: null, state: "failed" };
  }

  // AI parsing takes precedence over text-extraction status.
  if (attachment.is_ai_parsing === true) {
    return { attachment, doc: null, state: "preparing" };
  }

  // Check for a paired document BEFORE the status field. A matched document
  // message is definitive proof that processing completed — the status field
  // on the attachment payload can be stale ("processing") when list-messages
  // overwrites locally-synced data with a server snapshot that hasn't caught
  // up yet. Checking the doc first makes rendering self-healing: the card
  // clears its spinner as soon as the document WS event lands, regardless
  // of the attachment status race.
  const storageId = attachment.reference_doc_storage_id;
  const doc = Array.isArray(messages)
    ? messages.find((m) => m?.message_type === "document" && m?.reference_document_id === storageId) || null
    : null;

  if (doc) {
    if (attachment.word_limit_exceeded || attachment.page_limit_exceeded) {
      return { attachment, doc, state: "partial" };
    }
    return { attachment, doc, state: "ready" };
  }

  // No paired document yet. Only a genuinely in-progress extraction spins.
  // A `successful` (or any other non-"processing") status must NOT resolve
  // to "processing" — that was the bug that spun forever when V3 extraction
  // was skipped and no document ever arrived.
  if (attachment.status === "processing") {
    return { attachment, doc: null, state: "processing" };
  }

  return { attachment, doc: null, state: "no_preview" };
};

/**
 * Send-gate helpers. An attachment is "settled" for send purposes only once its
 * `/complete` call has returned a confirmed `storageId` (carried on its status
 * object) — NOT merely when its S3 upload bytes reach 100%. A terminally failed
 * attachment counts as settled too: it must not keep the Send button disabled.
 * Everything else (waiting for a presigned URL, uploading, or S3-done-but-not-yet-
 * confirmed) is unsettled and MUST gate send.
 *
 * These are the single source of truth shared by `ChatBoxComponent`'s
 * `hasUploadingAttachments` and `chatboxHelpers`' submit guard.
 *
 * @param {Object|undefined} status - one entry from state.messageSlice.attachmentStatuses
 * @returns {boolean} true when the attachment no longer gates send
 */
export const isAttachmentSettled = (status) => {
  if (status?.status === "failed") return true;
  return Boolean(status?.storageId);
};

/**
 * True when any composer attachment still lacks a confirmed `storageId` (failed
 * attachments are excluded). Drives both the disabled Send button and the
 * submit-time block-and-toast backstop.
 *
 * @param {Object} attachments - state.messageSlice.attachments (keyed by id)
 * @param {Object} attachmentStatuses - state.messageSlice.attachmentStatuses (keyed by id)
 * @returns {boolean}
 */
export const hasUnsettledAttachment = (attachments = {}, attachmentStatuses = {}) =>
  Object.values(attachments || {}).some((att) => !isAttachmentSettled(attachmentStatuses?.[att.id]));

/**
 * Collects the confirmed storageIds to send, skipping terminally failed attachments.
 * Never yields a null/undefined entry, so `reference_doc_storage_ids` can't carry one.
 *
 * @param {Object} attachments - state.messageSlice.attachments (keyed by id)
 * @param {Object} attachmentStatuses - state.messageSlice.attachmentStatuses (keyed by id)
 * @returns {Array<string>}
 */
export const collectConfirmedStorageIds = (attachments = {}, attachmentStatuses = {}) =>
  Object.values(attachments || {}).reduce((ids, att) => {
    const status = attachmentStatuses?.[att.id];
    if (status?.status === "failed") return ids;
    const storageId = status?.storageId || att.storageId;
    if (storageId) ids.push(storageId);
    return ids;
  }, []);

/**
 * Gets unique attachment storage IDs from a thread
 * @param {Object} threadData - The thread data object from Redux state
 * @returns {Array<string>} Array of unique storage IDs
 */
export const getUniqueAttachmentStorageIds = (threadData) => {
  const storageIds = new Set();

  // Add reference doc storage ID
  if (threadData?.reference_doc?.reference_doc_storage_id) {
    storageIds.add(threadData.reference_doc.reference_doc_storage_id);
  }

  // Add storage IDs from messages
  if (threadData?.messages && Array.isArray(threadData.messages)) {
    threadData.messages.forEach((message) => {
      if (message?.payload?.attachments && Array.isArray(message.payload.attachments)) {
        message.payload.attachments.forEach((att) => {
          if (att.reference_doc_storage_id || att.storage_id) {
            storageIds.add(att.reference_doc_storage_id || att.storage_id);
          }
        });
      }
    });
  }

  return Array.from(storageIds);
};
